import { runAz } from "./az.js";
import { getAzAccount } from "./auth.js";
import { discoverGateway } from "./discovery.js";
import { readFoundryConfig, readSeedEndpoint, writeFoundryConfig } from "./foundry-config.js";
import { saveProfile, readProfile, slugifyTenant, type Profile } from "./profiles.js";
import { resolveGatewayContext } from "./gateway-context.js";
import { setAzdFollowAz } from "./azd.js";
import { LocalCliError } from "./errors.js";

export interface SwitchResult {
  tenantId: string;
  clientId: string;
  projectEndpoint: string;
  subscriptionId: string;
  /** Profile name the OUTGOING config was snapshotted as (null if nothing to save). */
  snapshot: string | null;
}

/** Snapshot the current config.yaml + az context to a profile so a switch is
 *  reversible. Returns the saved name, or null if there's nothing coherent. */
async function snapshotOutgoing(asName?: string): Promise<string | null> {
  const az = await getAzAccount().catch(() => null);
  const cfg = await readFoundryConfig();
  if (!az || !cfg) return null;
  const seed = await readSeedEndpoint();
  const profile: Profile = {
    name: asName ?? slugifyTenant(cfg.tenantId),
    savedAt: new Date().toISOString(),
    subscriptionId: az.subscriptionId,
    tenantId: az.tenantId,
    config: { tenantId: cfg.tenantId, clientId: cfg.clientId, projectEndpoint: cfg.projectEndpoint },
    seedEndpoint: seed ?? cfg.projectEndpoint,
  };
  return await saveProfile(profile);
}

export async function discoverySwitch(subscription: string, asName?: string): Promise<SwitchResult> {
  const subs = JSON.parse(await runAz(["account", "list", "--all", "--output", "json"])) as {
    id: string;
    name: string;
    tenantId: string;
  }[];
  const target = subs.find((s) => s.id === subscription || s.name === subscription);
  if (!target) {
    throw new LocalCliError({
      code: "SWITCH_SUB_NOT_FOUND",
      message: `Subscription '${subscription}' is not in your az login.`,
      hint: "Run 'az login --tenant <tenant-id> --use-device-code' for the target directory, then retry.",
    });
  }
  // Snapshot the OUTGOING context (old sub + old config.yaml) BEFORE switching az.
  const snapshot = await snapshotOutgoing(asName);
  await runAz(["account", "set", "--subscription", target.id]);
  const gw = await discoverGateway({ interactive: false, subscriptionId: target.id });
  if (!gw.projectEndpoint) {
    throw new LocalCliError({
      code: "SWITCH_NO_ENDPOINT",
      message: `The gateway in '${target.name}' has no FOUNDRY_PROJECT_ENDPOINT env var.`,
      hint: "Set it manually: 'm8t config set projectEndpoint <url>'.",
    });
  }
  await writeFoundryConfig({
    tenantId: gw.gatewayTenantId,
    clientId: gw.gatewayClientId,
    projectEndpoint: gw.projectEndpoint,
  });
  await resolveGatewayContext({ interactive: false, forceRediscover: true });
  await setAzdFollowAz();
  return {
    tenantId: gw.gatewayTenantId,
    clientId: gw.gatewayClientId,
    projectEndpoint: gw.projectEndpoint,
    subscriptionId: target.id,
    snapshot,
  };
}

export async function profileSwitch(name: string, asName?: string): Promise<SwitchResult> {
  const prof = await readProfile(name);
  if (!prof) {
    throw new LocalCliError({
      code: "SWITCH_PROFILE_NOT_FOUND",
      message: `No saved profile '${name}'.`,
      hint: "List profiles with 'm8t switch --list'.",
    });
  }
  const snapshot = await snapshotOutgoing(asName);
  await runAz(["account", "set", "--subscription", prof.subscriptionId]).catch(() => undefined);
  await writeFoundryConfig(prof.config);
  await resolveGatewayContext({ interactive: false, forceRediscover: true }).catch(() => undefined);
  await setAzdFollowAz();
  return {
    tenantId: prof.config.tenantId,
    clientId: prof.config.clientId,
    projectEndpoint: prof.config.projectEndpoint,
    subscriptionId: prof.subscriptionId,
    snapshot,
  };
}
